import { observer } from "mobx-react-lite";
import React, { ChangeEvent, useCallback, useState } from "react";

import dateService from "../../../../../services/date-service/DateService";
import useErrorHandler, {
  ApplicationError,
} from "../../../../../services/error-handler/ErrorHandlerService";
import { snackbarNotificationsService } from "../../../../../services/notifications/SnackbarNotificationsService";
import { translationService } from "../../../../../services/translation/TranslationService";
import { CancelButtonProps } from "./CancelDowngradeButton";
import useThemeService from "../../../../../services/theme-service/ThemeService";
import ActionButton from "./ActionButton";
import RadioButton from "../../../../../components/radio-button/RadioButton";
import "./CancelSubscriptionModal.scss";

export const CancelSubscriptionButton = observer((props: CancelButtonProps) => {
  const { organizationSubscriptionPlansStorage } = props;
  const [dataManageOption, setDataManageOption] = useState("self-manage");

  const { themeClass } = useThemeService();
  const { handleErrorInSnackbar } = useErrorHandler();

  const handleCancelSubscription = useCallback(async () => {
    try {
      await organizationSubscriptionPlansStorage.cancelSubscription(
        dataManageOption === "automatic-manage"
      );
      snackbarNotificationsService.showSuccess(
        dataManageOption === "automatic-manage"
          ? "SubscriptionPlans.card.cancel-subscription-success-automatic-deletion"
          : "SubscriptionPlans.card.cancel-subscription-success-manual-deletion"
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      handleErrorInSnackbar(applicationError);
    }
  }, [dataManageOption, handleErrorInSnackbar, organizationSubscriptionPlansStorage]);

  const handleSelectionChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDataManageOption(String(e.target.value));
  }, []);

  return (
    <ActionButton
      confirmationAction={handleCancelSubscription}
      className={themeClass(["btn-big", "fill-color-ghost"])}
      textKey={"SubscriptionPlans.card.cancel-subscription"}
      confirmationButtonTextKey={"SubscriptionPlans.card.cancel-subscription-confirmation"}
      confirmationModalTitleKey={"SubscriptionPlans.card.cancel-subscription-title"}
    >
      <div className="cancel-modal-container">
        <div>
          <span>
            {translationService.translate("SubscriptionPlans.card.cancel-subscription-modal.part1")}
            <b className="bolded-text">
              {dateService.formatDate(
                organizationSubscriptionPlansStorage.currentActiveSubscription.nextBillingDate
              )}
            </b>
            {translationService.translate("SubscriptionPlans.card.cancel-subscription-modal.part2")}
          </span>
        </div>
        <div>
          <RadioButton
            checked={dataManageOption === "self-manage"}
            value={"self-manage"}
            onChange={handleSelectionChange}
            labelKey="SubscriptionPlans.card.cancel-subscription-modal.radio-button-1"
          />
          {translationService.translate("SubscriptionPlans.card.cancel-subscription-modal.part3", {
            nextBillingDate: dateService.formatDate(
              organizationSubscriptionPlansStorage.currentActiveSubscription.nextBillingDate
            ),
            currentPlan: translationService.translate(`SubscriptionPlans.card.${organizationSubscriptionPlansStorage.currentActiveSubscription.name}`),
          })}
        </div>
        <div>
          <RadioButton
            checked={dataManageOption === "automatic-manage"}
            value={"automatic-manage"}
            onChange={handleSelectionChange}
            labelKey="SubscriptionPlans.card.cancel-subscription-modal.radio-button-2"
          />
          {translationService.translate("SubscriptionPlans.card.cancel-subscription-modal.part4", {
            nextBillingDate: dateService.formatDate(
              organizationSubscriptionPlansStorage.currentActiveSubscription.nextBillingDate
            ),
          })}
        </div>
      </div>
    </ActionButton>
  );
});
